import {Image, Tooltip, Typography} from "antd";
import styled from "styled-components";
import {DotLoading, getFileView, SkeletonView, T} from "./connector";
import {MessageType} from "./type";
import {useEffect, useState} from "react";
import {convertTimeMessageToText} from "./tencent";
import moment from "moment/moment";

async function imageUrlToBlobUrl(url) {
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    return blobUrl;
}

export default function MessageItem(props: {
    message: MessageType;
    isSender: boolean;
    onChange?: (key: string, value: any) => void;
    key?: string;
    blobs?: any
}) {
    const {message, isSender} = props;
    const [loading, setLoading] = useState(false);
    const [updater, setUpdate] = useState(0);

    useEffect(() => {
        const timer = setInterval(() => {
            try {
                setUpdate(Date.now());
            } catch (e) {
            }
        }, 5000);
        return () => {
            clearInterval(timer);
        }
    }, []);

    const renderFile = () => {
        const file = message?.files?.[0];
        let fileSizeText = "";
        if (file?.fileSize > 1024 * 1024) {
            fileSizeText = `${(file?.fileSize / (1024 * 1024)).toFixed(2)} MB`;
        } else {
            fileSizeText = `${(file?.fileSize / 1024).toFixed(2)} KB`;
        }

        const onGetFileView = async () => {
            if (loading) return;
            setLoading(true);
            const realUrl = (file?.file || "").split("?")[0];
            const result = await getFileView(realUrl);
            if (result.status === 200) {
                window.open(result?.data?.data, "_blank");
            }
            setLoading(false);
        }

        return (
            <MessageFile onClick={onGetFileView} className={isSender ? "sent" : "received"}>
                <IconMessageFile/>
                <MessageFileInfo>
                    <div className={'file-name'}>
                        {file?.fileName}
                        <span className={'file-extension'}>.{file?.fileExtension}</span>
                    </div>
                    <div className={'file-size'}>
                        {fileSizeText}
                        {loading ? (<DotLoading size={4} space={2}/>) : null}
                    </div>
                </MessageFileInfo>
            </MessageFile>
        )
    }

    const renderFileImage = () => {
        const file = message?.files?.[0];
        const urlImage = file?.fileView || file?.file;
        const urlImageKey = (urlImage || "").split("?")[0];

        const blob = message?.blobs?.[0] || props?.blobs[urlImageKey];
        return (
            <>
                <Image
                    fallback={"data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=="}
                    placeholder={(
                        <SkeletonView style={{width: '100%', height: 300}}/>
                    )}
                    data-src={blob || file.fileView || file?.file}
                    className={'image-item'}
                    src={blob || file.fileView || file?.file}
                    alt={file?.fileName}
                    style={{width: '100%', minHeight: 150, minWidth: 150}}
                    onError={(event) => {
                        console.log('load error', file);
                    }}
                />
            </>
        )
    }

    const renderText = () => {
        return (
            <MessageText ellipsis={{
                expandable: true,
                rows: 3,
                symbol: T("Read more"),
            }} className={'text'}>{message.text}</MessageText>
        )
    }

    if (message?.status === 'loading') {
        return (
            <MessageRow>
                {isSender ? <MessageSpace/> : null}
                <MessageContainer className={`${isSender ? 'sent' : 'received'} image none`}>
                    <SkeletonView style={{
                        height: 75,
                        width: (window.document.querySelector('.chat-container')?.clientWidth || window.innerWidth) * 0.6
                    }}/>
                </MessageContainer>
                {!isSender ? <MessageSpace/> : null}
            </MessageRow>
        )
    }


    // Hide message if no text and no file
    if (!message?.text && !message?.files?.length) {
        return null;
    }

    return (
        <MessageRow data-key={message?.id} data-updater={moment(updater).toISOString()}>
            {isSender ? <MessageSpace/> : null}
            <MessageContainer
                className={`${isSender ? 'sent' : 'received'} ${message.type} ${message?.status === 'error' ? 'error' : ''}`}>
                {
                    message.type === 'document' ? renderFile() : null
                }
                {
                    message.type === 'image' ? renderFileImage() : null
                }
                {
                    (message.type === 'text' || !message?.type) ? renderText() : null
                }
                <MessageFooter className={'footer'}>
                    <MessageTime className={'time'}>{
                        message?.status === 'sending' ? T("Sending...") : convertTimeMessageToText(message.timeInt)
                    }</MessageTime>
                    <MessageStatus></MessageStatus>
                </MessageFooter>


            </MessageContainer>

            {!isSender ? <MessageSpace/> : null}

            {
                message?.status === 'error' ? (
                    <MessageTextError><InfoIcon/> {T("Message not sent")}</MessageTextError>
                ) : null
            }
        </MessageRow>
    )
}

const MessageFile = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 8px;
    border-radius: 12px;
    cursor: pointer;

    &:hover {
    }

    svg {
        margin-top: 3px;
    }

    .file-name {
        color: #2C2D3A;
        font-size: 16px;
        font-weight: 600;
        line-height: 24px;
    }

    .file-extension {

    }

    .file-size {
        color: #727C8A;
        font-size: 10px;
        font-weight: 400;
    }

    &.sent {
        .file-name, svg *, .file-size {
            color: #ffffff;
            fill: #ffffff;
        }

        .dot {
            background-color: #ffffff;
        }
    }

`;
const MessageFileInfo = styled.div`
    .file-size {
        display: flex;
        align-items: center;

        & > div {
            margin-left: 4px;
        }
    }
`;

const MessageRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    position: relative;
`;
const MessageSpace = styled.div`
    min-width: 10%;
    flex: 1;
`;
const MessageContainer = styled.div`
    display: flex;
    padding: 12px 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    border-radius: 16px 16px 16px 0px;
    background: #FFF;
    margin-top: 16px;
    max-width: 90%;
    width: auto;
    overflow: hidden;

    &.error {
        margin-bottom: 24px;
    }

    &.sent {
        border-radius: 16px 16px 0px 16px;
        background: ${(props) => props.theme.main || "#1D896D"};

        .text {
            color: white;
        }

        .time {
            color: rgba(255, 255, 255, 0.6);
        }

        .footer {
            justify-content: flex-end;
        }

        .ant-typography-expand {
            color: rgba(255, 255, 255, 0.7);
        }
    }

    .ant-typography-expand {
        color: ${(props) => props.theme.main};
    }

    &.none {
        background-color: #f6f6f6;
    }

    &.image {
        padding: 0;
        overflow: hidden;
        position: relative;

        .image-item {
            width: 100%;
            height: auto;
            object-fit: cover;
            max-height: 300px;
        }

        .footer {
            position: absolute;
            left: 12px;
            bottom: 7px;
            border-radius: 4px;
            background: rgba(0, 0, 0, 0.40);
            height: 15px;
            width: auto;
            padding: 0 9px;
        }


    }

    &.sent.image {
        .footer {
            right: 12px;
            left: auto;
        }
    }
`;
const MessageText = styled(Typography.Paragraph)`
    color: #2C2D3A;
    font-size: 16px;
    font-weight: 300;
    white-space: pre-line;
    margin-bottom: 0 !important;
    width: 100%;
`;
const MessageTextError = styled(Typography.Paragraph)`
    color: #ef0000;
    font-size: 11px;
    font-weight: 300;
    white-space: nowrap;
    margin-bottom: 0 !important;
    position: absolute;
    bottom: 3px;
    right: 0px;
    display: flex;
    align-items: center;
    gap: 4px;

    svg {
        width: 12px;
        height: 12px;

        & * {
            fill: #ef0000;
        }
    }
`;

const MessageFooter = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
`;
const MessageTime = styled.div`
    color: #D0D1DB;
    font-size: 12px;
    font-weight: 500;
`;
const MessageStatus = styled.div``;
const IconMessageFile = (props) => (
    <svg {...props} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M15.8 2.21048C15.39 1.80048 14.68 2.08048 14.68 2.65048V6.14048C14.68 7.60048 15.92 8.81048 17.43 8.81048C18.38 8.82048 19.7 8.82048 20.83 8.82048C21.4 8.82048 21.7 8.15048 21.3 7.75048C19.86 6.30048 17.28 3.69048 15.8 2.21048Z"
            fill="#BDC3CB"/>
        <path
            d="M20.5 10.19H17.61C15.24 10.19 13.31 8.26 13.31 5.89V3C13.31 2.45 12.86 2 12.31 2H8.07C4.99 2 2.5 4 2.5 7.57V16.43C2.5 20 4.99 22 8.07 22H15.93C19.01 22 21.5 20 21.5 16.43V11.19C21.5 10.64 21.05 10.19 20.5 10.19ZM11.5 17.75H7.5C7.09 17.75 6.75 17.41 6.75 17C6.75 16.59 7.09 16.25 7.5 16.25H11.5C11.91 16.25 12.25 16.59 12.25 17C12.25 17.41 11.91 17.75 11.5 17.75ZM13.5 13.75H7.5C7.09 13.75 6.75 13.41 6.75 13C6.75 12.59 7.09 12.25 7.5 12.25H13.5C13.91 12.25 14.25 12.59 14.25 13C14.25 13.41 13.91 13.75 13.5 13.75Z"
            fill="#BDC3CB"/>
    </svg>
)

export const InfoIcon = (props) => {
    return (
        <svg {...props} width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M10.0001 1.66699C5.40841 1.66699 1.66675 5.40866 1.66675 10.0003C1.66675 14.592 5.40841 18.3337 10.0001 18.3337C14.5917 18.3337 18.3334 14.592 18.3334 10.0003C18.3334 5.40866 14.5917 1.66699 10.0001 1.66699ZM9.37508 6.66699C9.37508 6.32533 9.65842 6.04199 10.0001 6.04199C10.3417 6.04199 10.6251 6.32533 10.6251 6.66699V10.8337C10.6251 11.1753 10.3417 11.4587 10.0001 11.4587C9.65842 11.4587 9.37508 11.1753 9.37508 10.8337V6.66699ZM10.7667 13.6503C10.7251 13.7587 10.6667 13.842 10.5917 13.9253C10.5084 14.0003 10.4167 14.0587 10.3167 14.1003C10.2167 14.142 10.1084 14.167 10.0001 14.167C9.89175 14.167 9.78342 14.142 9.68342 14.1003C9.58341 14.0587 9.49175 14.0003 9.40842 13.9253C9.33342 13.842 9.27508 13.7587 9.23342 13.6503C9.19175 13.5503 9.16675 13.442 9.16675 13.3337C9.16675 13.2253 9.19175 13.117 9.23342 13.017C9.27508 12.917 9.33342 12.8253 9.40842 12.742C9.49175 12.667 9.58341 12.6087 9.68342 12.567C9.88342 12.4837 10.1167 12.4837 10.3167 12.567C10.4167 12.6087 10.5084 12.667 10.5917 12.742C10.6667 12.8253 10.7251 12.917 10.7667 13.017C10.8084 13.117 10.8334 13.2253 10.8334 13.3337C10.8334 13.442 10.8084 13.5503 10.7667 13.6503Z"
                fill="#727C8A"/>
        </svg>
    )
}
